import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  FlatList,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import { MessageCircle, ChevronRight } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY, CONTENT_MAX_WIDTH, withWeight } from '../../src/theme';
import { Header } from '@components/layout';
import { EmptyState, ErrorState, LoadingSpinner, useConfirm } from '@components/common';
import { PostCard } from '@components/posts';
import { usePostsStore } from '../../src/store/postsStore';
import { useStructuresStore } from '../../src/store/structuresStore';
import { useAuthStore } from '../../src/store/authStore';
import { Post, Structure } from '../../src/types';
import { getReadinessLevelFromPercent, getReadinessColor } from '../../src/utils/readiness';
import { suppressPairedBefores } from '../../src/utils/postPairing';

/**
 * Per-row wrapper so the callbacks passed to PostCard are stable per item.
 * Mirrors the pattern on the feed — lets React.memo on PostCard actually skip
 * re-renders when other posts in the list update.
 */
const ThreadPostRow = React.memo(function ThreadPostRow({
  post,
  isLiked,
  currentUserId,
  onOpen,
  onLike,
  onOpenComments,
  onEdit,
  onDelete,
  rowStyle,
}: {
  post: Post;
  isLiked: boolean;
  currentUserId?: string;
  onOpen: (id: string) => void;
  onLike: (id: string) => void;
  onOpenComments: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  rowStyle: StyleProp<ViewStyle>;
}) {
  const handleOpen = useCallback(() => onOpen(post.id), [onOpen, post.id]);
  const handleLike = useCallback(() => onLike(post.id), [onLike, post.id]);
  const handleComment = useCallback(() => onOpenComments(post.id), [onOpenComments, post.id]);
  const handleEdit = useCallback(() => onEdit(post.id), [onEdit, post.id]);
  const handleDelete = useCallback(() => onDelete(post.id), [onDelete, post.id]);
  return (
    <View style={rowStyle}>
      <PostCard
        post={post}
        isLiked={isLiked}
        currentUserId={currentUserId}
        onPress={handleOpen}
        onLike={handleLike}
        onComment={handleComment}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </View>
  );
});

export default function StructureThreadsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const user = useAuthStore((s) => s.user);
  // Narrow selectors: a bare usePostsStore() re-rendered this screen on
  // every store change, incl. background mapPosts pushes it never reads.
  const {
    structurePosts,
    structurePostsHasMore,
    isLoading,
    isLoadingMoreStructurePosts,
    likedPosts,
    error,
    fetchStructurePosts,
    fetchMoreStructurePosts,
    likePost,
    unlikePost,
    deletePost,
    clearError,
  } = usePostsStore(
    useShallow((s) => ({
      structurePosts: s.structurePosts,
      structurePostsHasMore: s.structurePostsHasMore,
      isLoading: s.isLoading,
      isLoadingMoreStructurePosts: s.isLoadingMoreStructurePosts,
      likedPosts: s.likedPosts,
      error: s.error,
      fetchStructurePosts: s.fetchStructurePosts,
      fetchMoreStructurePosts: s.fetchMoreStructurePosts,
      likePost: s.likePost,
      unlikePost: s.unlikePost,
      deletePost: s.deletePost,
      clearError: s.clearError,
    }))
  );

  const confirm = useConfirm();

  // Render-level suppression of paired-away before posts (screen/selector
  // altitude, not the store fetch — pagination hasMore math stays based on
  // unfiltered page sizes). See postPairing.ts.
  const visibleStructurePosts = useMemo(
    () => suppressPairedBefores(structurePosts),
    [structurePosts]
  );

  // The thread list alone gives no sense of *which* property you're looking
  // at — fetch the structure so the list can carry a context header.
  const [structure, setStructure] = useState<Structure | null>(null);
  const fetchStructure = useStructuresStore((s) => s.fetchStructure);
  useEffect(() => {
    if (!id) return;
    let stale = false;
    fetchStructure(id).then((s) => {
      if (!stale && s) setStructure(s);
    });
    return () => {
      stale = true;
    };
  }, [id, fetchStructure]);

  useEffect(() => {
    if (id) fetchStructurePosts(id, true);
  }, [id, fetchStructurePosts]);

  const likedPostsRef = useRef(likedPosts);
  useEffect(() => {
    likedPostsRef.current = likedPosts;
  }, [likedPosts]);

  const handleLike = useCallback(
    (postId: string) => {
      if (!user) return;
      if (likedPostsRef.current.has(postId)) {
        unlikePost(postId, user.id);
      } else {
        likePost(postId, user.id);
      }
    },
    [user, likePost, unlikePost]
  );

  const handleOpen = useCallback((postId: string) => router.push(`/post/${postId}`), []);
  const handleOpenComments = useCallback(
    (postId: string) => router.push(`/post/${postId}?openComments=1`),
    []
  );

  const handleEditPost = useCallback((postId: string) => {
    router.push(`/post/create?editPostId=${postId}`);
  }, []);

  const handleDeletePost = useCallback(
    async (postId: string) => {
      const post = structurePosts.find((p) => p.id === postId);
      if (!post || !user) return;
      const confirmed = await confirm({
        title: 'Delete observation?',
        message: 'This action cannot be undone.',
        confirmLabel: 'Delete',
        destructive: true,
      });
      if (!confirmed) return;
      try {
        await deletePost(post.id, post.authorId, post.structureId);
      } catch {
        // Error surfaced via store
      }
    },
    [structurePosts, user, confirm, deletePost]
  );

  const onEndReached = useCallback(() => {
    if (structurePostsHasMore && !isLoadingMoreStructurePosts) {
      fetchMoreStructurePosts();
    }
  }, [structurePostsHasMore, isLoadingMoreStructurePosts, fetchMoreStructurePosts]);

  const renderItem = useCallback(
    ({ item }: { item: Post }) => (
      <ThreadPostRow
        post={item}
        isLiked={likedPosts.has(item.id)}
        currentUserId={user?.id}
        onOpen={handleOpen}
        onLike={handleLike}
        onOpenComments={handleOpenComments}
        onEdit={handleEditPost}
        onDelete={handleDeletePost}
        rowStyle={styles.row}
      />
    ),
    [
      likedPosts,
      user?.id,
      handleOpen,
      handleLike,
      handleOpenComments,
      handleEditPost,
      handleDeletePost,
    ]
  );

  // Reached without a structure id (bad deep link / missing ?id= param):
  // say so instead of rendering a misleading "No observations yet" list.
  if (!id) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="All Observations" />
        <ErrorState
          message="This link is missing a property. Open a property from the map or search and try again."
          onRetry={() => router.replace('/')}
          retryLabel="Go to map"
        />
      </View>
    );
  }

  if (error && structurePosts.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="All Observations" />
        <ErrorState
          message={error}
          onRetry={() => {
            clearError();
            if (id) fetchStructurePosts(id, true);
          }}
        />
      </View>
    );
  }

  if (isLoading && structurePosts.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="All Observations" />
        <LoadingSpinner fullScreen message="Loading observations..." />
      </View>
    );
  }

  const pct = structure?.claimSummary?.progressPercent;
  const readinessColor =
    pct != null ? (getReadinessColor(getReadinessLevelFromPercent(pct), colors) ?? null) : null;

  const contextHeader = structure ? (
    <TouchableOpacity
      style={[styles.contextHeader, { borderBottomColor: colors.border }]}
      onPress={() => router.push(`/structure/${id}`)}
      accessibilityRole="button"
      accessibilityLabel="View property details">
      <View style={styles.contextBody}>
        <Text style={[styles.contextAddress, { color: colors.text }]} numberOfLines={1}>
          {structure.address || 'Property'}
        </Text>
        {structure.city ? (
          <Text style={[styles.contextCity, { color: colors.textSecondary }]} numberOfLines={1}>
            {structure.city}
          </Text>
        ) : null}
      </View>
      {pct != null && readinessColor ? (
        <Text style={[styles.contextReadiness, { color: readinessColor }]}>{Math.round(pct)}%</Text>
      ) : null}
      <ChevronRight size={18} color={colors.textTertiary} />
    </TouchableOpacity>
  ) : null;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Header showBack title="All Observations" />
      <FlatList
        data={visibleStructurePosts}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        ListHeaderComponent={contextHeader}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.5}
        removeClippedSubviews
        initialNumToRender={8}
        maxToRenderPerBatch={8}
        windowSize={7}
        contentContainerStyle={[
          styles.listContent,
          visibleStructurePosts.length === 0 && styles.emptyContent,
        ]}
        ListEmptyComponent={
          <EmptyState
            icon={MessageCircle}
            title="No observations yet"
            message="Be the first to share a progress or opportunity observation for this property."
          />
        }
        ListFooterComponent={
          isLoadingMoreStructurePosts ? (
            <LoadingSpinner size="small" style={styles.footer} color={colors.textSecondary} />
          ) : null
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  // 768 reading column centered in the desktop content pane (matches the
  // standard Header's constraint); no-op on mobile.
  listContent: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    alignSelf: 'center',
  },
  row: {
    paddingHorizontal: SPACING.md,
  },
  contextHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  contextBody: {
    flex: 1,
  },
  contextAddress: {
    ...withWeight(TYPOGRAPHY.body, 'semibold'),
  },
  contextCity: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 1,
  },
  contextReadiness: {
    ...TYPOGRAPHY.button,
    fontVariant: ['tabular-nums'],
  },
  emptyContent: {
    flexGrow: 1,
  },
  footer: {
    paddingVertical: SPACING.md,
  },
});
